import { StrokeBuilder, computeBbox, type Stroke, type Rect } from './stroke';
import { thinPoints, pressureToWidth } from './smoothing';
import { buildRibbon, fillRibbon } from './ribbon';
import { strokeHitByPath, splitStroke } from './eraser';
import { TileRenderer, pageRectFor } from './renderer';
import { createPdfUnderlayProvider } from './pdf';
import { pointInPolygon, selectStrokes, selectionBbox, transformPoints } from './select';
import { fitShape } from './shapes';
import { hitTextBox, textBoxFont, textBoxRect } from './text';
import { resolveInk, isHighlight, paperColor, type Theme } from '../theme/tokens';
import type { Repo } from '../store/repo';
import { newId } from '../store/repo';
import type { Image, Page, TextBox } from '../store/types';

export type Tool = 'pen' | 'highlighter' | 'eraser-stroke' | 'eraser-area' | 'lasso' | 'text';
const THIN_EPSILON = 0.35;

// ---- text tool (M3b) ----
const TEXT_TAP_SLOP_PX = 8;    // screen px: below = tap (emit textboxtap), at/above = drag
const TEXT_DEFAULT_W = 240;    // world px (spec §1.3)
const TEXT_DEFAULT_FONT = 15;  // world px (spec §1.3)

// ---- version history (M3b spec §3) ----
const HISTORY_MAX_EVENTS = 200;

// ---- images (M4 spec §3) ----
const IMAGE_TAP_SLOP_PX = 8;   // screen px: a lasso gesture under this travel is a tap
const IMAGE_MAX_DIM = 400;     // world px cap on either dimension at insert
const IMAGE_DECODE_MAX_PX = 2048; // decoded-bitmap cap (larger side), matches the tile-canvas ceiling

/**
 * Cap decoded-bitmap dimensions: scale the LARGER side down to `max`
 * (aspect preserved, rounded, never below 1px); within-cap dims pass through
 * unchanged. Pure core of the engine image loader's decode cap.
 */
export function capDimensions(w: number, h: number, max: number): { w: number; h: number } {
  const largest = Math.max(w, h);
  if (largest <= max || largest <= 0) return { w, h };
  const s = max / largest;
  return { w: Math.max(1, Math.round(w * s)), h: Math.max(1, Math.round(h * s)) };
}

// ---- lasso selection (M3a) ----
const HANDLE_CORNER_PX = 12;   // corner scale-handle size / hit radius, SCREEN px
const HANDLE_ROTATE_PX = 16;   // rotate-handle diameter / hit radius, SCREEN px
const ROTATE_OFFSET_PX = 24;   // rotate handle sits this far above top-center, SCREEN px
const DUPLICATE_OFFSET = 24;   // duplicate copies land +24/+24 WORLD px away
// Matches --accent / dark --accent in theme/tokens.css (canvas can't read CSS vars cheaply).
const ACCENT = { light: '#E8927C', dark: '#F0A48F' } as const;

// ---- shape-hold (M3a) ----
const HOLD_MS = 300;           // stillness duration that triggers recognition
const HOLD_MOVE_PX = 6;        // world px: movement beyond this re-arms (or reverts a snap)
const SNAP_EPSILON_PX = 6;     // screen px: RDP tolerance handed to fitShape

type UndoOp =
  | { type: 'add'; strokeId: string }
  | { type: 'erase'; strokeIds: string[] }
  | { type: 'split'; splits: Array<{ originalId: string; fragmentIds: string[] }> }
  | { type: 'add-batch'; strokeIds: string[] }
  | { type: 'transform'; pairs: Array<{ originalId: string; replacementId: string }> }
  // Text box create (before null) / delete (after null) / edit-move (both).
  | { type: 'textbox'; before: TextBox | null; after: TextBox | null }
  // History restore (spec §3): reversal tombstone ops in both directions,
  // batched into ONE op so a single undo returns to the pre-restore state.
  | { type: 'restore'; revivedIds: string[]; tombstonedIds: string[] }
  // Image insert (before null) / delete (after null) / move-scale (both) —
  // mirrors the textbox op exactly (M4 spec §3).
  | { type: 'image'; before: Image | null; after: Image | null };

/** Tool state captured at pointerup so later setTool/setColor calls can't re-route a pending commit. */
interface CommitSnapshot { tool: Tool; color: string; baseWidth: number; viewportScale: number; eraserRadius: number }

/** Accumulated selection transform about the ORIGINAL bbox center (uniform scale ⇒ one-call composition). */
interface SelTransform { dx: number; dy: number; scale: number; rotate: number }
interface SelTransformFull extends SelTransform { cx: number; cy: number }

interface SelectionState {
  strokes: Stroke[];   // ORIGINAL (untransformed) strokes; re-rendered via transformPoints each frame
  t: SelTransform;
  bbox: Rect;          // original selection bbox (untransformed)
  cx: number;          // original bbox center — the fixed pivot for scale/rotate
  cy: number;
}

interface SelectionGesture {
  pointerId: number;
  pointerType: string;
  mode: 'move' | 'scale' | 'rotate';
  x0: number;          // world coords at gesture start
  y0: number;
  t0: SelTransform;    // transform snapshot at gesture start
}

/**
 * Active image selection (M4 spec §3): the ORIGINAL record plus an accumulated
 * move/scale transform. Uniform scale about the (translated) center preserves
 * aspect by construction; no rotate in v1.
 */
interface ImageSelState {
  image: Image;                                // record as selected (untransformed)
  t: { dx: number; dy: number; scale: number };
}

interface ImageGesture {
  pointerId: number;
  pointerType: string;
  mode: 'move' | 'scale';
  x0: number;          // world coords at gesture start
  y0: number;
  t0: ImageSelState['t'];
}

/** Active text-tool pointer: becomes a move once travel ≥ TEXT_TAP_SLOP_PX, otherwise a tap at up. */
interface TextGesture {
  pointerId: number;
  pointerType: string;
  clientX0: number;    // screen px at down — tap/drag threshold is screen-space
  clientY0: number;
  x0: number;          // world coords at down
  y0: number;
  box: TextBox | null; // hit box at down (null = empty space)
  moved: boolean;
}

/** Serialized selection for the in-memory clipboard (appState owns storage). Points are bbox-origin-relative. */
export interface ClipboardStroke { tool: 'pen' | 'highlighter'; color: string; baseWidth: number; points: number[] }
export interface ClipboardPayload { strokes: ClipboardStroke[]; w: number; h: number }

export interface EngineEvents {
  strokeactive: (active: boolean) => void;
  viewport: (vp: { x: number; y: number; scale: number }) => void;
  historychange: (state: { canUndo: boolean; canRedo: boolean }) => void;
  selectionchange: (sel: { kind: 'strokes' | 'image'; count: number; bboxWorld: Rect } | null) => void;
  /** Text-tool tap (<8px travel): world coords + topmost hit box id (null = empty space). */
  textboxtap: (tap: { world: { x: number; y: number }; boxId: string | null }) => void;
  error: (message: string) => void;
}

export class InkEngine {
  private staticCanvas!: HTMLCanvasElement;
  private liveCanvas!: HTMLCanvasElement;
  private liveCtx!: CanvasRenderingContext2D;
  private staticCtx!: CanvasRenderingContext2D;
  private renderer!: TileRenderer;

  private tool: Tool = 'pen';
  private color = 'ink-charcoal';
  private baseWidth = 3;
  private eraserRadius = 12;
  private theme: Theme = 'light';

  private viewport = { x: 0, y: 0, scale: 1 };
  private builder: StrokeBuilder | null = null;
  private predicted: number[] = []; // [x,y,pressure,dt] look-ahead, transient
  private activePointer: number | null = null;
  private activePointerType: 'pen' | 'touch' | 'mouse' | null = null;
  private cachedRect: DOMRect | null = null; // host rect cached while a stroke/gesture is active
  private lastEventTime = 0;
  private penSeen = false;
  private singleFingerDraws = false;

  // two-finger pan/zoom
  private touches = new Map<number, { x: number; y: number }>();
  private pinchStart: { dist: number; scale: number; cx: number; cy: number; vx: number; vy: number } | null = null;

  private undoStack: UndoOp[] = [];
  private redoStack: UndoOp[] = [];
  private pending: Promise<unknown> = Promise.resolve();
  private listeners: { [K in keyof EngineEvents]?: EngineEvents[K][] } = {};
  private raf = 0;
  private staticRaf = 0;
  private liveDirty = false;
  private abort = new AbortController();
  private resizeObs: ResizeObserver | null = null;
  private unsaved: Stroke[] = []; // strokes whose initial save failed; retried once
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  // lasso selection
  private selection: SelectionState | null = null;
  private selGesture: SelectionGesture | null = null;
  // A single touch landing outside the bbox might be the start of a two-finger
  // pan — defer the commit decision until the finger lifts (tap), moves (pan),
  // or a second finger arrives (pinch).
  private pendingOutsideTap: { pointerId: number; x: number; y: number } | null = null;

  // images (M4): live records, ulid order = z-order (topmost last)
  private images: Image[] = [];
  private imageSel: ImageSelState | null = null;
  private imgGesture: ImageGesture | null = null;

  // text tool (M3b)
  private textBoxes: TextBox[] = [];               // live boxes, ulid order = z-order (topmost last)
  private textGesture: TextGesture | null = null;
  private measureCtx: CanvasRenderingContext2D | null = null; // scratch ctx for text measures

  // version history (M3b spec §3): non-null = preview mode (pointer input suspended)
  private historyStrokes: Stroke[] | null = null;

  // shape-hold
  private shapeSnapEnabled = true;
  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  private holdArmX = 0; // world coords of the last arm point
  private holdArmY = 0;
  private preSnap: Float32Array | null = null; // raw points stashed when a snap replaced the builder
  private snapCount = 0; // builder point count right after the snap swap

  /**
   * `opts.getFileBytes` (M4): authed cached read of an immutable R2 file —
   * the Editor passes `(id) => repo.getFile(id, fetcher)`. It powers the
   * image bitmap loader and the PDF underlay; when absent (tests, callers
   * without file plumbing) image records still load/manipulate but bitmaps
   * and PDF underlays gracefully never render.
   */
  static async create(
    host: HTMLElement, page: Page, repo: Repo,
    opts?: { getFileBytes?: (fileId: string) => Promise<ArrayBuffer> },
  ): Promise<InkEngine> {
    const e = new InkEngine(host, page, repo, opts?.getFileBytes);
    const settings = await repo.getSettings();
    e.penSeen = settings.penSeen;
    e.singleFingerDraws = settings.singleFingerDraws;
    e.color = settings.lastColor;
    e.baseWidth = settings.lastWidth;
    e.eraserRadius = settings.lastEraserRadius;
    e.shapeSnapEnabled = settings.shapeSnap;
    e.renderer.setStrokes(await repo.getActiveStrokes(page.id));
    e.textBoxes = await repo.getActiveTextBoxes(page.id); // topmost-last (ulid order)
    e.renderer.setTextBoxes(e.textBoxes);
    e.images = await repo.getActiveImages(page.id); // topmost-last (ulid order)
    e.renderer.setImages(e.images);
    if (page.kind === 'infinite' && page.viewport) e.viewport = { ...page.viewport };
    if (page.kind === 'fixed') e.fitPage(host);
    e.redrawStatic();
    return e;
  }

  private constructor(
    private host: HTMLElement, private page: Page, private repo: Repo,
    getFileBytes?: (fileId: string) => Promise<ArrayBuffer>,
  ) {
    this.staticCanvas = this.makeLayer(host, 0);
    this.liveCanvas = this.makeLayer(host, 1);
    this.staticCtx = this.staticCanvas.getContext('2d')!;
    this.liveCtx = this.liveCanvas.getContext('2d', { desynchronized: true }) ?? this.liveCanvas.getContext('2d')!;
    this.renderer = new TileRenderer(page);
    // Async bitmap/underlay completions repaint through the static scheduler.
    // An active image selection also repaints its live-layer overlay: the
    // selected image is excluded from tiles, so its just-loaded bitmap only
    // shows up via drawImageOverlay.
    this.renderer.onneedsredraw = () => {
      this.scheduleStatic();
      if (this.imageSel) this.drawImageOverlay();
    };
    if (getFileBytes) {
      // Image bitmaps: file bytes → decode, capped at IMAGE_DECODE_MAX_PX on
      // the larger side. A 25MB JPEG decodes to ~200MB RGBA — 3x the whole
      // AsyncBitmaps budget — so oversize decodes are re-decoded scaled and
      // the full-size bitmap is released. Requested lazily by the tile pass.
      this.renderer.setImageLoader(async (fileId) => {
        const full = await createImageBitmap(new Blob([await getFileBytes(fileId)]));
        const cap = capDimensions(full.width, full.height, IMAGE_DECODE_MAX_PX);
        if (cap.w === full.width && cap.h === full.height) return full;
        try {
          const scaled = await createImageBitmap(full, { resizeWidth: cap.w, resizeHeight: cap.h, resizeQuality: 'high' });
          full.close?.();
          return scaled;
        } catch {
          // Resize options unsupported (older engines): the full decode is
          // still correct, just budget-hungry — better than no image.
          return full;
        }
      });
      if (page.pdfRef) {
        const rect = pageRectFor(page); // pdfRef pages are 'fixed', so always defined
        if (rect) {
          this.renderer.setUnderlay(createPdfUnderlayProvider(
            page.pdfRef.fileId, page.pdfRef.pageNo, rect, getFileBytes,
            { invalidate: (r) => this.renderer.invalidateRect(r), redraw: () => this.scheduleStatic() },
          ));
        }
      }
    }
    const s = this.abort.signal;
    host.addEventListener('pointerdown', this.onDown, { signal: s });
    host.addEventListener('pointermove', this.onMove, { signal: s });
    host.addEventListener('pointerup', this.onUp, { signal: s });
    host.addEventListener('pointercancel', this.onCancel, { signal: s });
    // Suppress Safari's native gesture recognizers (selection loupe, callout,
    // Scribble/system copy gestures from palm contact) at the source.
    // Canceling touch events does NOT suppress the pointer events above.
    const blockNative = (e: Event) => e.preventDefault();
    host.addEventListener('touchstart', blockNative, { passive: false, signal: s });
    host.addEventListener('touchmove', blockNative, { passive: false, signal: s });
    host.style.touchAction = 'none';
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObs = new ResizeObserver(() => this.onResize());
      this.resizeObs.observe(host);
    }
  }

  private onResize() {
    const dpr = globalThis.devicePixelRatio ?? 1;
    for (const c of [this.staticCanvas, this.liveCanvas]) {
      c.width = Math.max(1, this.host.clientWidth * dpr);
      c.height = Math.max(1, this.host.clientHeight * dpr);
    }
    this.renderer.setDpr(dpr);
    this.cachedRect = null;
    this.clearLive();
    this.redrawStatic();
    if (this.selection) this.drawSelectionOverlay();
    if (this.imageSel) this.drawImageOverlay();
  }

  private makeLayer(host: HTMLElement, z: number): HTMLCanvasElement {
    const c = document.createElement('canvas');
    const dpr = globalThis.devicePixelRatio ?? 1;
    c.width = Math.max(1, host.clientWidth * dpr);
    c.height = Math.max(1, host.clientHeight * dpr);
    Object.assign(c.style, { position: 'absolute', inset: '0', width: '100%', height: '100%', zIndex: String(z) });
    host.appendChild(c);
    return c;
  }

  private fitPage(host: HTMLElement) {
    const pr = pageRectFor(this.page);
    if (!pr) return;
    const scale = Math.min(host.clientWidth / pr.w, host.clientHeight / pr.h) * 0.95;
    this.viewport = {
      scale,
      x: pr.x - (host.clientWidth / scale - pr.w) / 2,
      y: pr.y - (host.clientHeight / scale - pr.h) / 2,
    };
  }

  // ---------- events API ----------
  on<K extends keyof EngineEvents>(ev: K, cb: EngineEvents[K]) {
    ((this.listeners[ev] ??= []) as EngineEvents[K][]).push(cb);
  }
  private emit<K extends keyof EngineEvents>(ev: K, ...args: Parameters<EngineEvents[K]>) {
    for (const cb of this.listeners[ev] ?? []) (cb as any)(...args);
  }

  // ---------- public API ----------
  setTool(tool: Tool) {
    if (tool !== this.tool) {
      if (this.selection) void this.commitSelection();
      if (this.imageSel) void this.commitImageSelection();
    }
    this.tool = tool;
  }
  getTool(): Tool { return this.tool; }
  setShapeSnap(v: boolean) { this.shapeSnapEnabled = v; }
  setColor(color: string) { this.color = color; void this.repo.patchSettings({ lastColor: color }); }
  getColor() { return this.color; }
  setBaseWidth(w: number) { this.baseWidth = w; void this.repo.patchSettings({ lastWidth: w }); }
  getBaseWidth() { return this.baseWidth; }
  setEraserRadius(r: number) { this.eraserRadius = r; void this.repo.patchSettings({ lastEraserRadius: r }); }
  getEraserRadius() { return this.eraserRadius; }
  setSingleFingerDraws(v: boolean) { this.singleFingerDraws = v; }
  setTheme(theme: Theme) { this.theme = theme; this.renderer.setTheme(theme); this.redrawStatic(); if (this.selection) this.drawSelectionOverlay(); if (this.imageSel) this.drawImageOverlay(); }
  getViewport() { return { ...this.viewport }; }
  /** Awaitable barrier for all pending async commits (used by tests & page nav). */
  idle(): Promise<unknown> { return this.pending; }

  /**
   * Re-fetch committed strokes from the repo and redraw (used after remote
   * sync applies changes to this page). Callers must not invoke this while a
   * stroke is being written — the Editor defers to pen-up.
   *
   * During history preview the renderer swap is SKIPPED (a sync pull must not
   * clobber the as-of-T view). Nothing is lost: the remote rows are already in
   * the repo, and exitHistoryPreview()/restoreToTime() both re-read the repo
   * fresh, so the changes land the moment the preview closes.
   */
  async reloadStrokes(): Promise<void> {
    if (this.destroyed || this.historyStrokes !== null) return;
    // Serialize with the commit queue: a commit enqueued after this call was
    // made must not interleave with (or be clobbered by) the reload's fetch.
    this.enqueue(async () => {
      // Re-check preview in-job: enterHistoryPreview may have begun after this
      // reload was enqueued (its own `await pending` snapshots an earlier
      // chain link, so this job can still run after the preview is active).
      if (this.destroyed || this.historyStrokes !== null) return;
      const strokes = await this.repo.getActiveStrokes(this.page.id);
      if (this.destroyed || this.historyStrokes !== null) return;
      this.renderer.setStrokes(strokes);
      this.redrawStatic();
    });
    await this.pending;
  }

  async undo() {
    // A pending selection transform is the most recent user action — commit it
    // first so undo applies to it (and so the ops below can't touch strokes
    // that are mid-selection).
    if (this.selection) await this.commitSelection();
    if (this.imageSel) await this.commitImageSelection();
    const op = this.undoStack.pop();
    if (!op) return;
    try {
      await this.applyOp(op, true);
    } catch (err) {
      this.undoStack.push(op);
      this.emit('error', err instanceof Error ? err.message : String(err));
      return;
    }
    this.redoStack.push(op);
    this.emitHistory();
  }
  async redo() {
    if (this.selection) await this.commitSelection(); // non-identity commit clears redo, matching new-op semantics
    if (this.imageSel) await this.commitImageSelection();
    const op = this.redoStack.pop();
    if (!op) return;
    try {
      await this.applyOp(op, false);
    } catch (err) {
      this.redoStack.push(op);
      this.emit('error', err instanceof Error ? err.message : String(err));
      return;
    }
    this.undoStack.push(op);
    this.emitHistory();
  }
  private emitHistory() {
    this.emit('historychange', { canUndo: this.undoStack.length > 0, canRedo: this.redoStack.length > 0 });
  }

  private async applyOp(op: UndoOp, invert: boolean) {
    const del = (id: string, on: boolean) => {
      // keep any not-yet-persisted copy in sync so a later retry saves the tombstone state
      const u = this.unsaved.find((s) => s.id === id);
      if (u) { if (on) u.deletedAt = Date.now(); else delete u.deletedAt; }
      return this.repo.setStrokeDeleted(id, on ? Date.now() : null);
    };
    if (op.type === 'textbox') {
      // Undo/redo writes through the NORMAL repo paths, which journal: the
      // reversal syncs to other devices as a fresh edit (newer updatedAt wins
      // LWW there) — consistent with stroke undo behavior.
      const target = invert ? op.before : op.after;
      const removed = invert ? op.after : op.before;
      if (target) await this.repo.putTextBox({ ...target }); // put clears any tombstone (whole-record write)
      else if (removed) await this.repo.setTextBoxDeleted(removed.id, Date.now());
      await this.refreshTextBoxes();
      return; // strokes untouched — skip the stroke refresh below
    }
    if (op.type === 'image') {
      // Same journaled-reversal semantics as textbox ops (M4 spec §3).
      const target = invert ? op.before : op.after;
      const removed = invert ? op.after : op.before;
      if (target) await this.repo.putImage({ ...target }); // put clears any tombstone (whole-record write)
      else if (removed) await this.repo.setImageDeleted(removed.id, Date.now());
      await this.refreshImages();
      return; // strokes untouched — skip the stroke refresh below
    }
    if (op.type === 'add') await del(op.strokeId, invert);
    if (op.type === 'erase') for (const id of op.strokeIds) await del(id, !invert);
    if (op.type === 'split') {
      for (const sp of op.splits) {
        await del(sp.originalId, !invert);
        for (const id of sp.fragmentIds) await del(id, invert);
      }
    }
    if (op.type === 'add-batch') for (const id of op.strokeIds) await del(id, invert);
    if (op.type === 'restore') {
      // forward: revived get their tombstone cleared, tombstoned get one set
      for (const id of op.revivedIds) await del(id, invert);
      for (const id of op.tombstonedIds) await del(id, !invert);
    }
    if (op.type === 'transform') {
      // forward: originals tombstoned, replacements live; undo inverts both
      for (const p of op.pairs) {
        await del(p.originalId, !invert);
        await del(p.replacementId, invert);
      }
    }
    this.renderer.setStrokes(await this.repo.getActiveStrokes(this.page.id));
    this.redrawStatic();
  }

  // ---------- pointer pipeline ----------
  private onDown = (e: PointerEvent) => {
    if (this.historyStrokes !== null) return; // history preview: all pointer input suspended
    if (e.pointerType === 'pen' && !this.penSeen) {
      this.penSeen = true;
      void this.repo.patchSettings({ penSeen: true });
    }
    this.cachedRect ??= this.host.getBoundingClientRect();
    if (e.pointerType === 'touch') {
      this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
      // Palm rejection: while a pen stroke is in progress, record the touch and do
      // nothing else — no pinch, no pan, and never cancel the pen stroke.
      if (this.activePointer !== null && this.activePointerType === 'pen') return;
      // Same guard while a PEN drives a selection gesture (drag/scale/rotate):
      // a sliding palm must not pan, pinch, or arm an outside-tap commit.
      if (this.selGesture?.pointerType === 'pen') return;
      // ...and while a pen drives an image gesture (M4).
      if (this.imgGesture?.pointerType === 'pen') return;
      // ...and while a pen drags a text box.
      if (this.textGesture?.pointerType === 'pen') return;
      if (this.touches.size === 2) {
        this.pendingOutsideTap = null; // second finger: it's a pinch, not an outside tap
        if (this.selGesture?.pointerType === 'touch') this.selGesture = null; // keep the accumulated transform
        if (this.imgGesture?.pointerType === 'touch') this.imgGesture = null; // keep the accumulated transform
        if (this.textGesture?.pointerType === 'touch') { this.textGesture = null; this.clearLive(); } // pinch aborts a finger box-drag
        if (this.activePointerType === 'touch') this.cancelStroke();
        this.beginPinch();
        return;
      }
      // Single finger drives selection gestures while a selection is active.
      if (this.tool === 'lasso' && this.selection && this.activePointer === null && !this.selGesture) {
        const { x, y } = this.toWorld(e.clientX, e.clientY);
        const mode = this.hitTestSelection(x, y);
        if (mode) this.beginSelectionGesture(e, mode, x, y);
        else this.pendingOutsideTap = { pointerId: e.pointerId, x: e.clientX, y: e.clientY };
        return; // a finger never starts a lasso path
      }
      // ...and image gestures while an image selection is active (M4).
      if (this.tool === 'lasso' && this.imageSel && this.activePointer === null && !this.imgGesture) {
        const { x, y } = this.toWorld(e.clientX, e.clientY);
        const mode = this.hitTestImageSel(x, y);
        if (mode) this.beginImageGesture(e, mode, x, y);
        else this.pendingOutsideTap = { pointerId: e.pointerId, x: e.clientX, y: e.clientY };
        return; // a finger never starts a lasso path
      }
      if (!(this.singleFingerDraws && !this.penSeen)) return; // single-finger pan handled in onMove
    }
    if (this.activePointer !== null || this.selGesture !== null || this.textGesture !== null || this.imgGesture !== null) return;
    if (e.pointerType !== 'pen' && this.touches.size >= 2) return;
    if (e.pointerType === 'pen') this.pinchStart = null; // palm gesture, if any, ends at pen contact
    if (this.tool === 'lasso' && this.selection) {
      const { x, y } = this.toWorld(e.clientX, e.clientY);
      const mode = this.hitTestSelection(x, y);
      if (mode) { this.beginSelectionGesture(e, mode, x, y); return; }
      void this.commitSelection(); // outside tap commits; fall through to start a fresh lasso
    }
    if (this.tool === 'lasso' && this.imageSel) {
      const { x, y } = this.toWorld(e.clientX, e.clientY);
      const mode = this.hitTestImageSel(x, y);
      if (mode) { this.beginImageGesture(e, mode, x, y); return; }
      void this.commitImageSelection(); // outside tap commits; fall through to start a fresh lasso
    }
    if (this.tool === 'text') {
      // Text tool never inks: record the down point; up decides tap vs box-drag.
      const { x, y } = this.toWorld(e.clientX, e.clientY);
      this.textGesture = {
        pointerId: e.pointerId, pointerType: e.pointerType,
        clientX0: e.clientX, clientY0: e.clientY, x0: x, y0: y,
        box: this.hitTextBoxAt(x, y), moved: false,
      };
      try { this.host.setPointerCapture(e.pointerId); } catch { /* jsdom */ }
      return;
    }
    this.activePointer = e.pointerId;
    this.activePointerType = e.pointerType as 'pen' | 'touch' | 'mouse';
    try { this.host.setPointerCapture(e.pointerId); } catch { /* jsdom */ }
    this.builder = new StrokeBuilder();
    this.lastEventTime = e.timeStamp;
    const { x, y } = this.toWorld(e.clientX, e.clientY);
    this.builder.add(x, y, this.pressureOf(e), 0);
    if (this.tool === 'pen' && this.shapeSnapEnabled) this.armHold(x, y);
    this.liveDirty = true;
    this.scheduleLive();
    this.emit('strokeactive', true);
  };

  private onMove = (e: PointerEvent) => {
    if (this.historyStrokes !== null) return; // history preview: all pointer input suspended
    // active selection gesture (pen, mouse, or single finger)
    if (this.selGesture && e.pointerId === this.selGesture.pointerId) {
      if (e.pointerType === 'touch') this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.updateSelectionGesture(e);
      return;
    }
    // active image gesture (M4)
    if (this.imgGesture && e.pointerId === this.imgGesture.pointerId) {
      if (e.pointerType === 'touch') this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.updateImageGesture(e);
      return;
    }
    // active text-box drag (or not-yet-qualified text tap)
    if (this.textGesture && e.pointerId === this.textGesture.pointerId) {
      if (e.pointerType === 'touch') this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.updateTextGesture(e);
      return;
    }
    // touch pan/zoom (suppressed while a pen drives a selection or text-box gesture — palm rejection)
    if (e.pointerType === 'touch' && this.touches.has(e.pointerId) && this.activePointer === null && this.selGesture?.pointerType !== 'pen' && this.imgGesture?.pointerType !== 'pen' && this.textGesture?.pointerType !== 'pen') {
      const prev = this.touches.get(e.pointerId)!;
      this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.touches.size === 2 && this.pinchStart) { this.updatePinch(); return; }
      if (this.pendingOutsideTap?.pointerId === e.pointerId) {
        if (Math.hypot(e.clientX - this.pendingOutsideTap.x, e.clientY - this.pendingOutsideTap.y) < 8) return;
        this.pendingOutsideTap = null; // moved: it's a pan, not a tap — keep the selection
      }
      if (this.touches.size === 1 && !(this.singleFingerDraws && !this.penSeen)) {
        this.viewport.x -= (e.clientX - prev.x) / this.viewport.scale;
        this.viewport.y -= (e.clientY - prev.y) / this.viewport.scale;
        this.scheduleStatic();
        if (this.selection) this.drawSelectionOverlay();
        if (this.imageSel) this.drawImageOverlay();
        this.emit('viewport', this.getViewport());
      }
      return;
    }
    if (e.pointerId !== this.activePointer || !this.builder) return;
    const events: PointerEvent[] = typeof (e as any).getCoalescedEvents === 'function' ? ((e as any).getCoalescedEvents() as PointerEvent[]) : [e];
    for (const ce of events.length ? events : [e]) {
      const { x, y } = this.toWorld(ce.clientX, ce.clientY);
      const dt = Math.max(0, ce.timeStamp - this.lastEventTime);
      this.lastEventTime = ce.timeStamp;
      this.builder.add(x, y, this.pressureOf(ce), dt);
    }
    // Predicted tail (§7.4): short look-ahead segment, redrawn each frame,
    // never committed to the stroke.
    this.predicted.length = 0;
    if (typeof (e as any).getPredictedEvents === 'function') {
      for (const pe of (e as any).getPredictedEvents() as PointerEvent[]) {
        const w = this.toWorld(pe.clientX, pe.clientY);
        this.predicted.push(w.x, w.y, this.pressureOf(pe), 0);
      }
    }
    if (this.tool === 'pen' && this.shapeSnapEnabled) this.trackHold();
    this.liveDirty = true;
    this.scheduleLive();
  };

  private onUp = (e: PointerEvent) => {
    // History preview: a pointer that was down at entry had its stroke/gesture
    // dropped by enterHistoryPreview (and touches cleared) — its lift is void.
    if (this.historyStrokes !== null) return;
    if (e.pointerType === 'touch') {
      this.touches.delete(e.pointerId);
      if (this.touches.size < 2) this.pinchStart = null;
      if (this.pendingOutsideTap?.pointerId === e.pointerId) {
        this.pendingOutsideTap = null;
        // A genuine outside tap — but never commit under an in-progress
        // selection/image gesture (palm-first-then-pen sequence).
        if (!this.selGesture && !this.imgGesture) {
          void this.commitSelection();
          void this.commitImageSelection();
        }
      }
    }
    if (this.selGesture && e.pointerId === this.selGesture.pointerId) {
      this.selGesture = null;
      this.maybeReleaseRect();
      return;
    }
    if (this.imgGesture && e.pointerId === this.imgGesture.pointerId) {
      this.imgGesture = null;
      this.maybeReleaseRect();
      return;
    }
    if (this.textGesture && e.pointerId === this.textGesture.pointerId) {
      this.finishTextGesture(e);
      return;
    }
    if (e.pointerId !== this.activePointer || !this.builder) {
      this.maybeReleaseRect();
      return;
    }
    const { x, y } = this.toWorld(e.clientX, e.clientY);
    if (this.preSnap === null) {
      this.builder.add(x, y, this.pressureOf(e), Math.max(0, e.timeStamp - this.lastEventTime));
    } else {
      // Snap still active at lift: commit EXACTLY the recognized shape — drop
      // the lift point and any sub-threshold hand tremor appended since the
      // swap (movement past HOLD_MOVE_PX would already have reverted).
      this.builder.replaceWith(this.builder.toFloat32().subarray(0, this.snapCount * 4));
    }
    const raw = this.builder.toFloat32();
    const snap: CommitSnapshot = { tool: this.tool, color: this.color, baseWidth: this.baseWidth, viewportScale: this.viewport.scale, eraserRadius: this.eraserRadius };
    this.builder = null;
    this.activePointer = null;
    this.activePointerType = null;
    this.clearHold();
    this.maybeReleaseRect();
    this.clearLive();
    this.emit('strokeactive', false);
    if (snap.tool === 'lasso') { this.finishLasso(raw); return; }
    this.enqueue(() => this.commit(raw, snap));
  };

  private onCancel = (e: PointerEvent) => {
    if (this.selGesture?.pointerId === e.pointerId) this.selGesture = null;
    if (this.imgGesture?.pointerId === e.pointerId) this.imgGesture = null;
    if (this.textGesture?.pointerId === e.pointerId) { this.textGesture = null; this.clearLive(); }
    if (this.pendingOutsideTap?.pointerId === e.pointerId) this.pendingOutsideTap = null;
    if (e.pointerType === 'touch') { this.touches.delete(e.pointerId); this.pinchStart = null; }
    if (e.pointerId === this.activePointer) this.cancelStroke();
    this.maybeReleaseRect();
  };

  private cancelStroke() {
    this.builder = null;
    this.activePointer = null;
    this.activePointerType = null;
    this.clearHold();
    this.clearLive();
    if (this.selection) this.drawSelectionOverlay();
    if (this.imageSel) this.drawImageOverlay();
    this.emit('strokeactive', false);
  }

  /** Only for non-pen pointers is a default pressure substituted; pens report real values. */
  private pressureOf(e: PointerEvent): number {
    return e.pointerType === 'pen' ? e.pressure : (e.pressure || 0.5);
  }

  /** Drop the cached host rect once no stroke or touch gesture is active. */
  private maybeReleaseRect() {
    if (this.activePointer === null && this.touches.size === 0) this.cachedRect = null;
  }

  private beginPinch() {
    const [a, b] = [...this.touches.values()];
    if (!a || !b) return;
    const cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
    const w = this.toWorld(cx, cy);
    this.pinchStart = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale: this.viewport.scale, cx, cy, vx: w.x, vy: w.y };
  }

  private updatePinch() {
    const [a, b] = [...this.touches.values()];
    if (!a || !b || !this.pinchStart) return;
    const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
    const scale = Math.min(8, Math.max(0.1, this.pinchStart.scale * (dist / this.pinchStart.dist)));
    const cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
    // keep the world point under the pinch center fixed (client -> host-local coords)
    const r = this.cachedRect ?? this.host.getBoundingClientRect();
    this.viewport.scale = scale;
    this.viewport.x = this.pinchStart.vx - (cx - r.left) / scale;
    this.viewport.y = this.pinchStart.vy - (cy - r.top) / scale;
    this.scheduleStatic();
    if (this.selection) this.drawSelectionOverlay(); // selection is world-coords; just redraw
    if (this.imageSel) this.drawImageOverlay();
    this.emit('viewport', this.getViewport());
  }

  // ---------- commit & erase ----------
  private enqueue(job: () => Promise<void>) {
    this.pending = this.pending.then(job).catch((err) => {
      this.emit('error', err instanceof Error ? err.message : String(err));
    });
  }

  private async commit(raw: Float32Array, snap: CommitSnapshot) {
    if (snap.tool === 'eraser-stroke' || snap.tool === 'eraser-area') return this.erase(raw, snap);
    // Thin in world units relative to on-screen size: at high zoom, small world
    // deviations are visible, so shrink epsilon accordingly (clamped).
    const epsilon = Math.max(0.05, THIN_EPSILON / snap.viewportScale);
    const points = thinPoints(raw, epsilon);
    const width = snap.tool === 'highlighter' ? snap.baseWidth * 4 : snap.baseWidth;
    const bbox = computeBbox(points, pressureToWidth(1, width));
    const color = snap.tool === 'highlighter' && !isHighlight(snap.color) ? 'highlight-yellow' : snap.color;
    const stroke: Stroke = {
      id: newId(),
      pageId: this.page.id,
      tool: snap.tool === 'highlighter' ? 'highlighter' : 'pen',
      color,
      baseWidth: width,
      points, bbox,
      createdAt: Date.now(),
    };
    // Ink stays visible and undoable even if persistence fails; save is retried below.
    this.renderer.addStroke(stroke);
    this.undoStack.push({ type: 'add', strokeId: stroke.id });
    this.redoStack = [];
    this.emitHistory();
    this.redrawStatic();
    try {
      await this.repo.putStroke(stroke);
    } catch {
      this.unsaved.push(stroke);
      this.scheduleRetry();
    }
  }

  /** Drain parked unsaved strokes through the commit queue (awaitable via idle()). */
  private flushUnsaved() {
    this.enqueue(async () => {
      try {
        const drained = this.unsaved.length > 0;
        while (this.unsaved.length) {
          await this.repo.putStroke(this.unsaved[0]!);
          this.unsaved.shift();
        }
        // Re-sync the renderer from the store after a successful drain so
        // recovered strokes reappear consistently (tombstone state included).
        // Skipped post-destroy: the renderer and canvases are already gone.
        if (drained && !this.destroyed) {
          this.renderer.setStrokes(await this.repo.getActiveStrokes(this.page.id));
          this.redrawStatic();
        }
      } catch (err) {
        this.emit('error', err instanceof Error ? err.message : String(err));
      }
    });
  }

  /** One delayed retry for strokes whose initial save failed; emits 'error' only if the retry also fails. */
  private scheduleRetry() {
    if (this.retryTimer !== null) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.flushUnsaved();
    }, 2000);
  }

  private async erase(raw: Float32Array, snap: CommitSnapshot) {
    const strokes = await this.repo.getActiveStrokes(this.page.id);
    const eraserBbox = computeBbox(raw, snap.eraserRadius * 2);
    if (snap.tool === 'eraser-stroke') {
      const hit: string[] = [];
      for (const s of strokes) {
        if (!rectsOverlap(s.bbox, eraserBbox)) continue;
        if (strokeHitByPath(s.points, raw, snap.eraserRadius + s.baseWidth / 2)) hit.push(s.id);
      }
      if (!hit.length) return;
      const now = Date.now();
      for (const id of hit) await this.repo.setStrokeDeleted(id, now);
      this.undoStack.push({ type: 'erase', strokeIds: hit });
    } else {
      // area eraser: split each hit stroke; one undo op for the whole gesture
      const splits: Array<{ originalId: string; fragmentIds: string[] }> = [];
      for (const s of strokes) {
        if (!rectsOverlap(s.bbox, eraserBbox)) continue;
        const r = snap.eraserRadius + s.baseWidth / 2;
        if (!strokeHitByPath(s.points, raw, r)) continue;
        const frags = splitStroke(s.points, raw, r);
        const fragIds: string[] = [];
        for (const f of frags) {
          const frag: Stroke = {
            id: newId(), pageId: s.pageId, tool: s.tool, color: s.color, baseWidth: s.baseWidth,
            points: f, bbox: computeBbox(f, pressureToWidth(1, s.baseWidth)), createdAt: Date.now(),
          };
          await this.repo.putStroke(frag);
          fragIds.push(frag.id);
        }
        await this.repo.setStrokeDeleted(s.id, Date.now());
        splits.push({ originalId: s.id, fragmentIds: fragIds });
      }
      if (!splits.length) return;
      this.undoStack.push({ type: 'split', splits });
    }
    this.redoStack = [];
    this.emitHistory();
    this.renderer.setStrokes(await this.repo.getActiveStrokes(this.page.id));
    this.redrawStatic();
  }

  // ---------- rendering ----------
  private toWorld(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.cachedRect ?? this.host.getBoundingClientRect();
    return {
      x: this.viewport.x + (clientX - rect.left) / this.viewport.scale,
      y: this.viewport.y + (clientY - rect.top) / this.viewport.scale,
    };
  }

  private scheduleLive() {
    if (this.raf) return;
    const loop = () => {
      this.raf = 0;
      if (this.liveDirty) { this.drawLive(); this.liveDirty = false; }
      if (this.builder) this.raf = requestAnimationFrame(loop);
    };
    this.raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(loop) : (loop(), 0);
  }

  /** rAF-gated static redraw: at most one renderer pass per frame during pan/pinch. */
  private scheduleStatic() {
    if (this.staticRaf) return;
    if (typeof requestAnimationFrame !== 'function') { this.redrawStatic(); return; }
    this.staticRaf = requestAnimationFrame(() => {
      this.staticRaf = 0;
      this.redrawStatic();
    });
  }

  private drawLive() {
    if (!this.builder) return;
    const dpr = globalThis.devicePixelRatio ?? 1;
    const ctx = this.liveCtx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.liveCanvas.width, this.liveCanvas.height);
    ctx.setTransform(dpr * this.viewport.scale, 0, 0, dpr * this.viewport.scale, -this.viewport.x * dpr * this.viewport.scale, -this.viewport.y * dpr * this.viewport.scale);
    if (this.tool === 'eraser-stroke' || this.tool === 'eraser-area') {
      // Eraser leaves no ink — draw a size-indicator ring at the current tip.
      const view = this.builder.view();
      const n = view.length;
      if (n >= 4) {
        const x = view[n - 4]!, y = view[n - 3]!;
        const ink = resolveInk('ink-charcoal', this.theme);
        ctx.beginPath();
        ctx.arc(x, y, this.eraserRadius, 0, Math.PI * 2);
        ctx.globalAlpha = 0.08;
        ctx.fillStyle = ink;
        ctx.fill();
        ctx.globalAlpha = 0.5;
        ctx.strokeStyle = ink;
        ctx.lineWidth = 1.5 / this.viewport.scale;
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
      return;
    }
    if (this.tool === 'lasso') {
      // Freeform lasso path: dashed accent polyline, no ink.
      const view = this.builder.view();
      if (view.length >= 8) {
        const vs = this.viewport.scale;
        ctx.beginPath();
        ctx.moveTo(view[0]!, view[1]!);
        for (let i = 4; i + 1 < view.length; i += 4) ctx.lineTo(view[i]!, view[i + 1]!);
        ctx.strokeStyle = this.theme === 'dark' ? ACCENT.dark : ACCENT.light;
        ctx.lineWidth = 1.5 / vs;
        ctx.setLineDash([6 / vs, 4 / vs]);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      return;
    }
    const width = this.tool === 'highlighter' ? this.baseWidth * 4 : this.baseWidth;
    const color = this.tool === 'highlighter' && !isHighlight(this.color) ? 'highlight-yellow' : this.color;
    ctx.fillStyle = resolveInk(color, this.theme);
    if (this.tool === 'highlighter') ctx.globalCompositeOperation = 'multiply';
    fillRibbon(ctx, buildRibbon(this.builder.view(), width));
    // predicted tail: last real point + predicted points, drawn fainter
    if (this.predicted.length && this.builder.count > 0) {
      const view = this.builder.view();
      const n = view.length;
      const tail = new Float32Array(4 + this.predicted.length);
      tail.set(view.subarray(n - 4, n), 0);
      tail.set(this.predicted, 4);
      ctx.globalAlpha = 0.6;
      fillRibbon(ctx, buildRibbon(tail, width));
      ctx.globalAlpha = 1;
    }
    ctx.globalCompositeOperation = 'source-over';
  }

  private clearLive() {
    this.liveCtx.setTransform(1, 0, 0, 1, 0, 0);
    this.liveCtx.clearRect(0, 0, this.liveCanvas.width, this.liveCanvas.height);
  }

  private redrawStatic() {
    this.renderer.draw(this.staticCtx, this.viewport, this.host.clientWidth, this.host.clientHeight);
  }

  /** Save infinite-page viewport (called by UI on page exit). */
  async persistViewport() {
    if (this.page.kind === 'infinite') await this.repo.savePageViewport(this.page.id, this.getViewport());
  }

  jumpToContent() {
    // center on union of stroke bboxes
    const strokes = this.renderer.getStrokes();
    if (!strokes.length) { this.viewport = { x: 0, y: 0, scale: 1 }; this.redrawStatic(); return; }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const s of strokes) {
      minX = Math.min(minX, s.bbox.x); minY = Math.min(minY, s.bbox.y);
      maxX = Math.max(maxX, s.bbox.x + s.bbox.w); maxY = Math.max(maxY, s.bbox.y + s.bbox.h);
    }
    const w = this.host.clientWidth, h = this.host.clientHeight;
    const scale = Math.min(2, Math.min(w / (maxX - minX + 80), h / (maxY - minY + 80)));
    this.viewport = { scale, x: minX - (w / scale - (maxX - minX)) / 2, y: minY - (h / scale - (maxY - minY)) / 2 };
    this.redrawStatic();
    if (this.selection) this.drawSelectionOverlay();
    if (this.imageSel) this.drawImageOverlay();
    this.emit('viewport', this.getViewport());
  }

  // ---------- lasso selection ----------

  /** Current selection summary for the UI (bbox in WORLD coords, transform applied). */
  getSelection(): { kind: 'strokes' | 'image'; count: number; bbox: Rect } | null {
    if (this.imageSel) return { kind: 'image', count: 1, bbox: this.imageSelRect(this.imageSel) };
    const sel = this.selection;
    return sel ? { kind: 'strokes', count: sel.strokes.length, bbox: this.currentSelectionBbox(sel) } : null;
  }

  /** At most one selection kind is active at a time (image tap only fires when no stroke selection results). */
  private emitSelection() {
    if (this.imageSel) {
      this.emit('selectionchange', { kind: 'image', count: 1, bboxWorld: this.imageSelRect(this.imageSel) });
      return;
    }
    const sel = this.selection;
    this.emit('selectionchange', sel ? { kind: 'strokes', count: sel.strokes.length, bboxWorld: this.currentSelectionBbox(sel) } : null);
  }

  /**
   * Lasso pointer-up: close the path and select strokes with >50% of points
   * inside. A TAP (<8 screen px travel — too short to enclose anything)
   * falls back to image hit-testing, topmost first (M4 spec §3).
   */
  private finishLasso(raw: Float32Array) {
    if (this.tool !== 'lasso') return; // tool switched mid-gesture: never select under another tool
    const poly: number[] = [];
    for (let i = 0; i + 3 < raw.length; i += 4) poly.push(raw[i]!, raw[i + 1]!);
    if (poly.length >= 2 && this.polyTravelPx(poly) < IMAGE_TAP_SLOP_PX) {
      const im = this.hitImageAt(poly[0]!, poly[1]!);
      if (im) this.setImageSelection(im);
      return;
    }
    if (poly.length < 6) return; // need ≥3 vertices to enclose anything
    const picked = selectStrokes(this.renderer.getStrokes(), poly);
    if (!picked.length) return; // empty selection: tool stays armed, nothing happens
    this.setSelection(picked);
  }

  /** Max SCREEN-px distance of any vertex from the first (tap vs path discrimination). */
  private polyTravelPx(poly: number[]): number {
    let max = 0;
    for (let i = 2; i + 1 < poly.length; i += 2) {
      max = Math.max(max, Math.hypot(poly[i]! - poly[0]!, poly[i + 1]! - poly[1]!));
    }
    return max * this.viewport.scale;
  }

  /** Install `strokes` as the active selection (identity transform). */
  private setSelection(strokes: Stroke[]) {
    const bbox = selectionBbox(strokes);
    this.selection = {
      strokes,
      t: { dx: 0, dy: 0, scale: 1, rotate: 0 },
      bbox,
      cx: bbox.x + bbox.w / 2,
      cy: bbox.y + bbox.h / 2,
    };
    this.renderer.setExcluded(new Set(strokes.map((s) => s.id)));
    this.redrawStatic();
    this.drawSelectionOverlay();
    this.emitSelection();
  }

  /**
   * Commit the active selection. Identity transform → just clear. Otherwise
   * each stroke is rewritten (transformed points, new id) and the original
   * tombstoned — ONE `transform` undo op for the whole selection. Runs through
   * the pending queue, so `idle()` (page nav, tests) covers it. The Editor
   * calls this before navigation; tool switches and destroy() call it too.
   */
  commitSelection(): Promise<unknown> {
    const sel = this.selection;
    if (!sel) return this.pending;
    this.selection = null;
    this.selGesture = null;
    this.pendingOutsideTap = null;
    this.emitSelection();
    const t = sel.t;
    const identity = Math.abs(t.dx) < 0.01 && Math.abs(t.dy) < 0.01 && Math.abs(t.scale - 1) < 1e-3 && Math.abs(t.rotate) < 1e-3;
    if (identity) {
      this.renderer.setExcluded(new Set());
      if (!this.destroyed) {
        this.clearLive();
        this.redrawStatic();
      }
      return this.pending;
    }
    const full: SelTransformFull = { ...t, cx: sel.cx, cy: sel.cy };
    this.enqueue(async () => {
      // sel.strokes is read at job time: an earlier queued restyle may have
      // swapped in restyled replacements for the originals.
      const now = Date.now();
      const pairs: Array<{ originalId: string; replacementId: string }> = [];
      for (const s of sel.strokes) {
        const points = transformPoints(s.points, full);
        const repl: Stroke = {
          id: newId(), pageId: s.pageId, tool: s.tool, color: s.color, baseWidth: s.baseWidth,
          points, bbox: computeBbox(points, pressureToWidth(1, s.baseWidth)), createdAt: now,
        };
        await this.repo.putStroke(repl);
        await this.repo.setStrokeDeleted(s.id, now);
        pairs.push({ originalId: s.id, replacementId: repl.id });
      }
      this.undoStack.push({ type: 'transform', pairs });
      this.redoStack = [];
      this.emitHistory();
      if (this.destroyed) return;
      this.renderer.setExcluded(new Set());
      this.renderer.setStrokes(await this.repo.getActiveStrokes(this.page.id));
      this.clearLive();
      this.redrawStatic();
    });
    return this.pending;
  }

  /** Tombstone every selected stroke (one `erase` undo op) and clear the selection. */
  deleteSelection() {
    const sel = this.selection;
    if (!sel) return;
    this.selection = null;
    this.selGesture = null;
    this.pendingOutsideTap = null;
    this.emitSelection();
    this.enqueue(async () => {
      const now = Date.now();
      const ids = sel.strokes.map((s) => s.id);
      for (const id of ids) await this.repo.setStrokeDeleted(id, now);
      this.undoStack.push({ type: 'erase', strokeIds: ids });
      this.redoStack = [];
      this.emitHistory();
      if (this.destroyed) return;
      this.renderer.setExcluded(new Set());
      this.renderer.setStrokes(await this.repo.getActiveStrokes(this.page.id));
      this.clearLive();
      this.redrawStatic();
    });
  }

  /** Insert +24/+24 copies of the current (visual) selection; the copies become the new selection. */
  duplicateSelection() {
    const sel = this.selection;
    if (!sel) return;
    const full: SelTransformFull = { ...sel.t, cx: sel.cx, cy: sel.cy };
    const specs = sel.strokes.map((s) => ({
      tool: s.tool, color: s.color, baseWidth: s.baseWidth,
      points: transformPoints(s.points, { ...full, dx: full.dx + DUPLICATE_OFFSET, dy: full.dy + DUPLICATE_OFFSET }),
    }));
    void this.commitSelection(); // settle any pending transform first (identity → no write)
    this.insertBatch(specs);
  }

  /**
   * Serialize the selection for the clipboard (points relative to the visual
   * bbox origin). Returns the payload — the engine does NOT store it; appState
   * owns the clipboard so it survives page navigation.
   */
  copySelection(): ClipboardPayload | null {
    const sel = this.selection;
    if (!sel) return null;
    const full: SelTransformFull = { ...sel.t, cx: sel.cx, cy: sel.cy };
    const transformed = sel.strokes.map((s) => ({ s, pts: transformPoints(s.points, full) }));
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const { pts } of transformed) {
      for (let i = 0; i + 3 < pts.length; i += 4) {
        const x = pts[i]!, y = pts[i + 1]!;
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
      }
    }
    if (!isFinite(minX)) return null;
    const strokes: ClipboardStroke[] = transformed.map(({ s, pts }) => {
      const rel = new Array<number>(pts.length);
      for (let i = 0; i + 3 < pts.length; i += 4) {
        rel[i] = pts[i]! - minX;
        rel[i + 1] = pts[i + 1]! - minY;
        rel[i + 2] = pts[i + 2]!;
        rel[i + 3] = pts[i + 3]!;
      }
      return { tool: s.tool, color: s.color, baseWidth: s.baseWidth, points: rel };
    });
    return { strokes, w: maxX - minX, h: maxY - minY };
  }

  /** Insert clipboard strokes centered in the viewport; the pasted set becomes the selection (lasso tool). */
  pasteClipboard(payload: ClipboardPayload) {
    if (!payload?.strokes?.length) return;
    if (this.selection) void this.commitSelection();
    if (this.imageSel) void this.commitImageSelection();
    this.tool = 'lasso'; // pasted set becomes an active selection; UI mirrors via getTool()
    const ox = this.viewport.x + this.host.clientWidth / (2 * this.viewport.scale) - payload.w / 2;
    const oy = this.viewport.y + this.host.clientHeight / (2 * this.viewport.scale) - payload.h / 2;
    const specs = payload.strokes.map((cs) => {
      const pts = new Float32Array(cs.points.length);
      for (let i = 0; i + 3 < cs.points.length; i += 4) {
        pts[i] = cs.points[i]! + ox;
        pts[i + 1] = cs.points[i + 1]! + oy;
        pts[i + 2] = cs.points[i + 2]!;
        pts[i + 3] = cs.points[i + 3]!;
      }
      return { tool: cs.tool, color: cs.color, baseWidth: cs.baseWidth, points: pts };
    });
    this.insertBatch(specs);
  }

  /** Shared duplicate/paste tail: put strokes, one `add-batch` op, batch becomes the selection. */
  private insertBatch(specs: Array<{ tool: Stroke['tool']; color: string; baseWidth: number; points: Float32Array }>) {
    this.enqueue(async () => {
      const copies: Stroke[] = [];
      for (const sp of specs) {
        const s: Stroke = {
          id: newId(), pageId: this.page.id, tool: sp.tool, color: sp.color, baseWidth: sp.baseWidth,
          points: sp.points, bbox: computeBbox(sp.points, pressureToWidth(1, sp.baseWidth)), createdAt: Date.now(),
        };
        await this.repo.putStroke(s);
        copies.push(s);
      }
      this.undoStack.push({ type: 'add-batch', strokeIds: copies.map((c) => c.id) });
      this.redoStack = [];
      this.emitHistory();
      if (this.destroyed) return;
      this.renderer.setStrokes(await this.repo.getActiveStrokes(this.page.id));
      this.setSelection(copies);
    });
  }

  /**
   * Rewrite the selected strokes with a new color and/or width (tombstone +
   * replacement, same `transform` pairs op) — syncs and undoes identically to
   * a transform. The selection stays active on the replacements, preserving
   * any accumulated (uncommitted) transform.
   */
  restyleSelection(style: { color?: string; baseWidth?: number }) {
    const sel = this.selection;
    if (!sel || (style.color === undefined && style.baseWidth === undefined)) return;
    this.enqueue(async () => {
      const now = Date.now();
      const pairs: Array<{ originalId: string; replacementId: string }> = [];
      const replacements: Stroke[] = [];
      for (const s of sel.strokes) {
        const baseWidth = style.baseWidth ?? s.baseWidth;
        const repl: Stroke = {
          id: newId(), pageId: s.pageId, tool: s.tool, color: style.color ?? s.color, baseWidth,
          points: s.points, bbox: computeBbox(s.points, pressureToWidth(1, baseWidth)), createdAt: now,
        };
        await this.repo.putStroke(repl);
        await this.repo.setStrokeDeleted(s.id, now);
        pairs.push({ originalId: s.id, replacementId: repl.id });
        replacements.push(repl);
      }
      this.undoStack.push({ type: 'transform', pairs });
      this.redoStack = [];
      this.emitHistory();
      // Point the selection object at the replacements even if it was already
      // committed/cleared: a queued commit job reads sel.strokes after us and
      // must transform the restyled strokes, not the tombstoned originals.
      sel.strokes = replacements;
      if (this.destroyed) return;
      if (this.selection === sel) this.renderer.setExcluded(new Set(replacements.map((r) => r.id)));
      this.renderer.setStrokes(await this.repo.getActiveStrokes(this.page.id));
      this.redrawStatic();
      if (this.selection === sel) {
        this.drawSelectionOverlay();
        this.emitSelection();
      }
    });
  }

  // ----- selection geometry & gestures -----

  private xformPoint(px: number, py: number, t: SelTransformFull): { x: number; y: number } {
    const cos = Math.cos(t.rotate), sin = Math.sin(t.rotate);
    const sx = (px - t.cx) * t.scale, sy = (py - t.cy) * t.scale;
    return { x: t.cx + sx * cos - sy * sin + t.dx, y: t.cy + sx * sin + sy * cos + t.dy };
  }

  /** The ORIGINAL bbox's 4 corners under the accumulated transform (a rotated rect). */
  private selCorners(sel: SelectionState): Array<{ x: number; y: number }> {
    const full: SelTransformFull = { ...sel.t, cx: sel.cx, cy: sel.cy };
    const { x, y, w, h } = sel.bbox;
    return [
      this.xformPoint(x, y, full),
      this.xformPoint(x + w, y, full),
      this.xformPoint(x + w, y + h, full),
      this.xformPoint(x, y + h, full),
    ];
  }

  /** Axis-aligned world bounds of the transformed selection (for UI positioning). */
  private currentSelectionBbox(sel: SelectionState): Rect {
    const c = this.selCorners(sel);
    const xs = c.map((p) => p.x), ys = c.map((p) => p.y);
    const minX = Math.min(...xs), minY = Math.min(...ys);
    return { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY };
  }

  /** Rotate handle: ROTATE_OFFSET_PX screen px beyond the transformed top-center, along the rotated "up". */
  private rotateHandlePos(sel: SelectionState): { x: number; y: number } {
    const full: SelTransformFull = { ...sel.t, cx: sel.cx, cy: sel.cy };
    const top = this.xformPoint(sel.bbox.x + sel.bbox.w / 2, sel.bbox.y, full);
    const cx = sel.cx + sel.t.dx, cy = sel.cy + sel.t.dy; // transformed center
    let dx = top.x - cx, dy = top.y - cy;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) { dx = 0; dy = -1; } else { dx /= len; dy /= len; }
    const off = ROTATE_OFFSET_PX / this.viewport.scale;
    return { x: top.x + dx * off, y: top.y + dy * off };
  }

  /** Hit-test in SCREEN px so handles stay tappable at any zoom. Priority: rotate > corners > inside. */
  private hitTestSelection(wx: number, wy: number): SelectionGesture['mode'] | null {
    const sel = this.selection;
    if (!sel) return null;
    const vs = this.viewport.scale;
    const rh = this.rotateHandlePos(sel);
    if (Math.hypot(wx - rh.x, wy - rh.y) * vs <= HANDLE_ROTATE_PX) return 'rotate';
    const corners = this.selCorners(sel);
    for (const c of corners) {
      if (Math.hypot(wx - c.x, wy - c.y) * vs <= HANDLE_CORNER_PX) return 'scale';
    }
    const poly: number[] = [];
    for (const c of corners) poly.push(c.x, c.y);
    return pointInPolygon(wx, wy, poly) ? 'move' : null;
  }

  private beginSelectionGesture(e: PointerEvent, mode: SelectionGesture['mode'], wx: number, wy: number) {
    // A gesture supersedes any armed outside-tap (e.g. a palm resting outside
    // the bbox before the pen lands) — its lift must not commit mid-gesture.
    this.pendingOutsideTap = null;
    this.selGesture = { pointerId: e.pointerId, pointerType: e.pointerType, mode, x0: wx, y0: wy, t0: { ...this.selection!.t } };
    try { this.host.setPointerCapture(e.pointerId); } catch { /* jsdom */ }
  }

  private updateSelectionGesture(e: PointerEvent) {
    const sel = this.selection, g = this.selGesture;
    if (!sel || !g) return;
    const { x, y } = this.toWorld(e.clientX, e.clientY);
    const t = sel.t;
    if (g.mode === 'move') {
      t.dx = g.t0.dx + (x - g.x0);
      t.dy = g.t0.dy + (y - g.y0);
    } else {
      // scale/rotate pivot on the (translated) center, which these gestures keep fixed
      const cx = sel.cx + g.t0.dx, cy = sel.cy + g.t0.dy;
      if (g.mode === 'scale') {
        const d0 = Math.hypot(g.x0 - cx, g.y0 - cy) || 1;
        t.scale = Math.min(50, Math.max(0.05, g.t0.scale * (Math.hypot(x - cx, y - cy) / d0)));
      } else {
        t.rotate = g.t0.rotate + Math.atan2(y - cy, x - cx) - Math.atan2(g.y0 - cy, g.x0 - cx);
      }
    }
    this.drawSelectionOverlay();
    this.emitSelection();
  }

  /** Selected ribbons (transformed, accent glow underneath) + bbox chrome on the live layer. */
  private drawSelectionOverlay() {
    const sel = this.selection;
    if (!sel || this.destroyed) return;
    const dpr = globalThis.devicePixelRatio ?? 1;
    const vs = this.viewport.scale;
    const ctx = this.liveCtx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.liveCanvas.width, this.liveCanvas.height);
    ctx.setTransform(dpr * vs, 0, 0, dpr * vs, -this.viewport.x * dpr * vs, -this.viewport.y * dpr * vs);
    const accent = this.theme === 'dark' ? ACCENT.dark : ACCENT.light;
    const full: SelTransformFull = { ...sel.t, cx: sel.cx, cy: sel.cy };
    for (const s of sel.strokes) {
      const pts = transformPoints(s.points, full);
      ctx.globalAlpha = 0.25; // soft accent glow under the ribbon
      ctx.fillStyle = accent;
      fillRibbon(ctx, buildRibbon(pts, s.baseWidth + 8 / vs));
      ctx.globalAlpha = 1;
      ctx.save();
      if (s.tool === 'highlighter' || isHighlight(s.color)) ctx.globalCompositeOperation = 'multiply';
      ctx.fillStyle = resolveInk(s.color, this.theme);
      fillRibbon(ctx, buildRibbon(pts, s.baseWidth));
      ctx.restore();
    }
    // chrome: dashed bbox, 4 corner squares, rotate handle (sizes in screen px → /vs)
    const corners = this.selCorners(sel);
    ctx.strokeStyle = accent;
    ctx.lineWidth = 1.5 / vs;
    ctx.setLineDash([6 / vs, 4 / vs]);
    ctx.beginPath();
    ctx.moveTo(corners[0]!.x, corners[0]!.y);
    for (let i = 1; i < 4; i++) ctx.lineTo(corners[i]!.x, corners[i]!.y);
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);
    const rh = this.rotateHandlePos(sel);
    const top = this.xformPoint(sel.bbox.x + sel.bbox.w / 2, sel.bbox.y, full);
    ctx.beginPath();
    ctx.moveTo(top.x, top.y);
    ctx.lineTo(rh.x, rh.y);
    ctx.stroke();
    const hs = HANDLE_CORNER_PX / vs;
    ctx.fillStyle = paperColor(this.theme);
    for (const c of corners) {
      ctx.fillRect(c.x - hs / 2, c.y - hs / 2, hs, hs);
      ctx.strokeRect(c.x - hs / 2, c.y - hs / 2, hs, hs);
    }
    ctx.beginPath();
    ctx.arc(rh.x, rh.y, HANDLE_ROTATE_PX / 2 / vs, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }

  // ---------- images (M4 spec §3) ----------

  /** Topmost live image containing the world point (ulid order = z-order, topmost last). */
  private hitImageAt(wx: number, wy: number): Image | null {
    for (let i = this.images.length - 1; i >= 0; i--) {
      const im = this.images[i]!;
      if (im.deletedAt) continue;
      if (wx >= im.x && wx <= im.x + im.w && wy >= im.y && wy <= im.y + im.h) return im;
    }
    return null;
  }

  /**
   * Install an image as the active selection (identity transform). The image
   * is hidden from tile raster and drawn (with chrome) on the live layer so
   * move/scale never re-rasterizes tiles per frame — same idiom as stroke
   * selections' setExcluded.
   */
  private setImageSelection(im: Image) {
    this.imageSel = { image: { ...im }, t: { dx: 0, dy: 0, scale: 1 } };
    this.renderer.setImages(this.images.filter((i) => i.id !== im.id));
    this.redrawStatic();
    this.drawImageOverlay();
    this.emitSelection();
  }

  /** Current world rect of the selected image under its accumulated transform. */
  private imageSelRect(sel: ImageSelState): Rect {
    const im = sel.image, t = sel.t;
    const cx = im.x + im.w / 2 + t.dx, cy = im.y + im.h / 2 + t.dy;
    const w = im.w * t.scale, h = im.h * t.scale;
    return { x: cx - w / 2, y: cy - h / 2, w, h };
  }

  /** Hit-test in SCREEN px (handles stay tappable at any zoom). Corners scale, inside moves. */
  private hitTestImageSel(wx: number, wy: number): ImageGesture['mode'] | null {
    const sel = this.imageSel;
    if (!sel) return null;
    const vs = this.viewport.scale;
    const r = this.imageSelRect(sel);
    const corners = [
      { x: r.x, y: r.y }, { x: r.x + r.w, y: r.y },
      { x: r.x + r.w, y: r.y + r.h }, { x: r.x, y: r.y + r.h },
    ];
    for (const c of corners) {
      if (Math.hypot(wx - c.x, wy - c.y) * vs <= HANDLE_CORNER_PX) return 'scale';
    }
    return wx >= r.x && wx <= r.x + r.w && wy >= r.y && wy <= r.y + r.h ? 'move' : null;
  }

  private beginImageGesture(e: PointerEvent, mode: ImageGesture['mode'], wx: number, wy: number) {
    // A gesture supersedes any armed outside-tap (palm-first-then-pen) —
    // its lift must not commit mid-gesture. Mirrors beginSelectionGesture.
    this.pendingOutsideTap = null;
    this.imgGesture = { pointerId: e.pointerId, pointerType: e.pointerType, mode, x0: wx, y0: wy, t0: { ...this.imageSel!.t } };
    try { this.host.setPointerCapture(e.pointerId); } catch { /* jsdom */ }
  }

  private updateImageGesture(e: PointerEvent) {
    const sel = this.imageSel, g = this.imgGesture;
    if (!sel || !g) return;
    const { x, y } = this.toWorld(e.clientX, e.clientY);
    const t = sel.t;
    if (g.mode === 'move') {
      t.dx = g.t0.dx + (x - g.x0);
      t.dy = g.t0.dy + (y - g.y0);
    } else {
      // Corner drag = uniform scale about the (translated) center — aspect
      // preserved by construction (spec: aspect-preserving handles, no rotate).
      const im = sel.image;
      const cx = im.x + im.w / 2 + g.t0.dx, cy = im.y + im.h / 2 + g.t0.dy;
      const d0 = Math.hypot(g.x0 - cx, g.y0 - cy) || 1;
      t.scale = Math.min(50, Math.max(0.05, g.t0.scale * (Math.hypot(x - cx, y - cy) / d0)));
    }
    this.drawImageOverlay();
    this.emitSelection();
  }

  /**
   * Commit the active image selection. Identity transform → just restore the
   * tile raster. Otherwise putImage rewrites the record IN PLACE (same id;
   * restamped updatedAt syncs as LWW) with ONE `image {before,after}` undo
   * op. Runs through the pending queue so idle() covers it.
   */
  commitImageSelection(): Promise<unknown> {
    const sel = this.imageSel;
    if (!sel) return this.pending;
    this.imageSel = null;
    this.imgGesture = null;
    this.pendingOutsideTap = null;
    this.emitSelection();
    const t = sel.t;
    const identity = Math.abs(t.dx) < 0.01 && Math.abs(t.dy) < 0.01 && Math.abs(t.scale - 1) < 1e-3;
    if (identity) {
      if (!this.destroyed) {
        this.renderer.setImages(this.images);
        this.clearLive();
        this.redrawStatic();
      }
      return this.pending;
    }
    const r = this.imageSelRect(sel);
    this.enqueue(async () => {
      const before = { ...sel.image };
      const after = { ...sel.image, x: r.x, y: r.y, w: r.w, h: r.h };
      await this.repo.putImage(after); // restamps after.updatedAt in place
      this.undoStack.push({ type: 'image', before, after: { ...after } });
      this.redoStack = [];
      this.emitHistory();
      if (this.destroyed) return;
      this.clearLive();
      await this.refreshImages();
    });
    return this.pending;
  }

  /** Tombstone the selected image (one `image` undo op, after: null) and clear the selection. */
  deleteImageSelection() {
    const sel = this.imageSel;
    if (!sel) return;
    this.imageSel = null;
    this.imgGesture = null;
    this.pendingOutsideTap = null;
    this.emitSelection();
    this.enqueue(async () => {
      const before = await this.repo.getImage(sel.image.id);
      if (before && !before.deletedAt) {
        await this.repo.setImageDeleted(before.id, Date.now());
        this.undoStack.push({ type: 'image', before: { ...before }, after: null });
        this.redoStack = [];
        this.emitHistory();
      }
      if (this.destroyed) return;
      this.clearLive();
      await this.refreshImages();
    });
  }

  /**
   * Place an uploaded image centered in the viewport, capped at
   * IMAGE_MAX_DIM world px on its larger side (aspect preserved; smaller
   * images keep natural size). One `image` undo op; the new image becomes an
   * active selection under the lasso tool (UI mirrors via getTool()).
   */
  async insertImage(fileId: string, naturalW: number, naturalH: number): Promise<Image> {
    if (this.selection) void this.commitSelection();
    if (this.imageSel) void this.commitImageSelection();
    this.tool = 'lasso';
    const s = Math.min(1, IMAGE_MAX_DIM / Math.max(1, naturalW, naturalH));
    const w = naturalW * s, h = naturalH * s;
    const cx = this.viewport.x + this.host.clientWidth / (2 * this.viewport.scale);
    const cy = this.viewport.y + this.host.clientHeight / (2 * this.viewport.scale);
    const now = Date.now();
    const img: Image = {
      id: newId(), pageId: this.page.id, fileId,
      x: cx - w / 2, y: cy - h / 2, w, h,
      createdAt: now, updatedAt: now,
    };
    this.enqueue(async () => {
      await this.repo.putImage(img); // stamps img.updatedAt in place
      this.undoStack.push({ type: 'image', before: null, after: { ...img } });
      this.redoStack = [];
      this.emitHistory();
      if (this.destroyed) return;
      await this.refreshImages();
      this.setImageSelection(img);
    });
    await this.pending;
    return img;
  }

  /**
   * Re-fetch live images from the repo and redraw — mirrors reloadTextBoxes
   * (enqueue-serialized). Safe during history preview: images are NOT part of
   * history (like text boxes); only the image layer swaps, the tile redraw
   * re-renders whatever stroke set the renderer currently holds.
   */
  async reloadImages(): Promise<void> {
    if (this.destroyed) return;
    this.enqueue(async () => this.refreshImages());
    await this.pending;
  }

  /** In-queue refresh: repo → engine list + renderer (+ redraw); keeps a selected image excluded from tiles. */
  private async refreshImages(): Promise<void> {
    if (this.destroyed) return;
    const images = await this.repo.getActiveImages(this.page.id);
    if (this.destroyed) return;
    this.images = images;
    const selId = this.imageSel?.image.id;
    this.renderer.setImages(selId === undefined ? images : images.filter((i) => i.id !== selId));
    this.redrawStatic();
  }

  /** Selected image (bitmap if loaded) + dashed bbox + 4 corner handles on the live layer. */
  private drawImageOverlay() {
    const sel = this.imageSel;
    if (!sel || this.destroyed) return;
    const dpr = globalThis.devicePixelRatio ?? 1;
    const vs = this.viewport.scale;
    const ctx = this.liveCtx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.liveCanvas.width, this.liveCanvas.height);
    ctx.setTransform(dpr * vs, 0, 0, dpr * vs, -this.viewport.x * dpr * vs, -this.viewport.y * dpr * vs);
    const r = this.imageSelRect(sel);
    const bmp = this.renderer.bitmaps.get('img:' + sel.image.fileId);
    if (bmp) ctx.drawImage(bmp as CanvasImageSource, r.x, r.y, r.w, r.h);
    else this.renderer.requestImageBitmap(sel.image); // excluded from tiles → overlay owns the request
    const accent = this.theme === 'dark' ? ACCENT.dark : ACCENT.light;
    ctx.strokeStyle = accent;
    ctx.lineWidth = 1.5 / vs;
    ctx.setLineDash([6 / vs, 4 / vs]);
    ctx.strokeRect(r.x, r.y, r.w, r.h);
    ctx.setLineDash([]);
    const hs = HANDLE_CORNER_PX / vs;
    ctx.fillStyle = paperColor(this.theme);
    const corners = [
      { x: r.x, y: r.y }, { x: r.x + r.w, y: r.y },
      { x: r.x + r.w, y: r.y + r.h }, { x: r.x, y: r.y + r.h },
    ];
    for (const c of corners) {
      ctx.fillRect(c.x - hs / 2, c.y - hs / 2, hs, hs);
      ctx.strokeRect(c.x - hs / 2, c.y - hs / 2, hs, hs);
    }
  }

  // ---------- text boxes (M3b spec §1.3) ----------

  /** Replace the engine's box list and mirror it to the renderer. Topmost-last order (ulid). */
  setTextBoxes(boxes: TextBox[]) {
    this.textBoxes = boxes.slice();
    this.renderer.setTextBoxes(boxes);
    this.redrawStatic();
  }

  /** Current live boxes (copy), topmost last — for the UI overlay. */
  getTextBoxes(): TextBox[] { return this.textBoxes.slice(); }

  /**
   * Re-fetch live text boxes from the repo and redraw — mirrors reloadStrokes
   * (enqueue-serialized so it can't interleave with a queued commit).
   * Safe during history preview: only the text layer is swapped; the tile
   * redraw re-renders whatever stroke set the renderer currently holds (the
   * preview's as-of-T set), so the ink preview is undisturbed.
   */
  async reloadTextBoxes(): Promise<void> {
    if (this.destroyed) return;
    this.enqueue(async () => this.refreshTextBoxes());
    await this.pending;
  }

  /** In-queue refresh: repo → engine list + renderer (+ redraw). Callers already inside a job use this. */
  private async refreshTextBoxes(): Promise<void> {
    if (this.destroyed) return;
    const boxes = await this.repo.getActiveTextBoxes(this.page.id);
    if (this.destroyed) return;
    this.textBoxes = boxes;
    this.renderer.setTextBoxes(boxes);
    this.redrawStatic();
  }

  /**
   * Create a text box at a world point with spec defaults (w 240, fontSize 15)
   * and the current ink color (highlight tokens fall back to charcoal — text
   * is ink, not highlighter). Persists, records an undo op, returns the box.
   */
  async createTextBoxAt(world: { x: number; y: number }): Promise<TextBox> {
    const now = Date.now();
    const box: TextBox = {
      id: newId(), pageId: this.page.id,
      x: world.x, y: world.y, w: TEXT_DEFAULT_W,
      text: '', fontSize: TEXT_DEFAULT_FONT, bold: false, italic: false,
      color: isHighlight(this.color) ? 'ink-charcoal' : this.color,
      createdAt: now, updatedAt: now,
    };
    this.enqueue(async () => {
      await this.repo.putTextBox(box); // stamps box.updatedAt in place
      this.undoStack.push({ type: 'textbox', before: null, after: { ...box } });
      this.redoStack = [];
      this.emitHistory();
      await this.refreshTextBoxes();
    });
    await this.pending;
    return box;
  }

  /** Persist an edited box (text/style/geometry); one `textbox` undo op with the prior record. */
  async updateTextBox(box: TextBox): Promise<void> {
    this.enqueue(async () => {
      const before = await this.repo.getTextBox(box.id);
      const after = { ...box };
      await this.repo.putTextBox(after);
      this.undoStack.push({ type: 'textbox', before: before ? { ...before } : null, after: { ...after } });
      this.redoStack = [];
      this.emitHistory();
      await this.refreshTextBoxes();
    });
    await this.pending;
  }

  /** Tombstone a box; one `textbox` undo op (after: null) so undo restores it. */
  async deleteTextBox(id: string): Promise<void> {
    this.enqueue(async () => {
      const before = await this.repo.getTextBox(id);
      if (!before || before.deletedAt) return; // already gone: nothing to do or undo
      await this.repo.setTextBoxDeleted(id, Date.now());
      this.undoStack.push({ type: 'textbox', before: { ...before }, after: null });
      this.redoStack = [];
      this.emitHistory();
      await this.refreshTextBoxes();
    });
    await this.pending;
  }

  private getMeasureCtx(): CanvasRenderingContext2D | null {
    if (!this.measureCtx) {
      const c = document.createElement('canvas');
      c.width = 1; c.height = 1;
      this.measureCtx = c.getContext('2d');
    }
    return this.measureCtx;
  }

  /** World rect of a box, measured with THIS box's font on the scratch ctx. */
  private textWorldRect(box: TextBox): Rect {
    const ctx = this.getMeasureCtx();
    if (!ctx) return { x: box.x, y: box.y, w: box.w, h: box.fontSize * 1.4 };
    ctx.font = textBoxFont(box);
    return textBoxRect(box, (s) => ctx.measureText(s).width);
  }

  /**
   * Topmost live box containing the world point. hitTextBox needs the font
   * set PER candidate box (per-box size/weight changes the wrapped height),
   * so iterate topmost-first here and hand it one box at a time.
   */
  private hitTextBoxAt(wx: number, wy: number): TextBox | null {
    const ctx = this.getMeasureCtx();
    for (let i = this.textBoxes.length - 1; i >= 0; i--) {
      const box = this.textBoxes[i]!;
      if (ctx) ctx.font = textBoxFont(box);
      const measure = ctx ? (s: string) => ctx.measureText(s).width : () => 0;
      if (hitTextBox([box], wx, wy, measure)) return box;
    }
    return null;
  }

  /** Per-move: qualify as a drag past the slop, then live-outline the box at its dragged position. */
  private updateTextGesture(e: PointerEvent) {
    const g = this.textGesture;
    if (!g) return;
    if (!g.moved && Math.hypot(e.clientX - g.clientX0, e.clientY - g.clientY0) < TEXT_TAP_SLOP_PX) return;
    g.moved = true;
    if (!g.box) return; // drag on empty space: no longer a tap, nothing to move
    const { x, y } = this.toWorld(e.clientX, e.clientY);
    this.drawTextDragOutline(g.box, x - g.x0, y - g.y0);
  }

  /** Pointer-up: <8px travel emits `textboxtap`; a drag on a hit box commits the move. */
  private finishTextGesture(e: PointerEvent) {
    const g = this.textGesture!;
    this.textGesture = null;
    this.maybeReleaseRect();
    const moved = g.moved || Math.hypot(e.clientX - g.clientX0, e.clientY - g.clientY0) >= TEXT_TAP_SLOP_PX;
    if (!moved) {
      // Tap: report the DOWN point (the up point may have wandered a few px).
      this.emit('textboxtap', { world: { x: g.x0, y: g.y0 }, boxId: g.box?.id ?? null });
      return;
    }
    this.clearLive();
    if (!g.box) return;
    const { x, y } = this.toWorld(e.clientX, e.clientY);
    const before = { ...g.box };
    const after = { ...g.box, x: g.box.x + (x - g.x0), y: g.box.y + (y - g.y0) };
    this.enqueue(async () => {
      await this.repo.putTextBox(after);
      this.undoStack.push({ type: 'textbox', before, after: { ...after } });
      this.redoStack = [];
      this.emitHistory();
      await this.refreshTextBoxes();
    });
  }

  /** Dashed accent outline of the box rect at its dragged position, on the live layer. */
  private drawTextDragOutline(box: TextBox, dx: number, dy: number) {
    const dpr = globalThis.devicePixelRatio ?? 1;
    const vs = this.viewport.scale;
    const ctx = this.liveCtx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.liveCanvas.width, this.liveCanvas.height);
    ctx.setTransform(dpr * vs, 0, 0, dpr * vs, -this.viewport.x * dpr * vs, -this.viewport.y * dpr * vs);
    const r = this.textWorldRect(box);
    ctx.strokeStyle = this.theme === 'dark' ? ACCENT.dark : ACCENT.light;
    ctx.lineWidth = 1.5 / vs;
    ctx.setLineDash([6 / vs, 4 / vs]);
    ctx.strokeRect(r.x + dx, r.y + dy, r.w, r.h);
    ctx.setLineDash([]);
  }

  // ---------- version history (M3b spec §3) ----------

  /**
   * Enter preview mode: loads ALL strokes (incl. tombstoned) once and returns
   * the event timeline — distinct createdAt/deletedAt stamps, ascending,
   * sampled down to ≤200 detents (first and last always kept). min = first
   * stroke createdAt, max = now. Pointer input is suspended until
   * exitHistoryPreview()/restoreToTime(). Ink only — text boxes are excluded
   * (their edits overwrite; spec §3).
   */
  async enterHistoryPreview(): Promise<{ min: number; max: number; events: number[] }> {
    // An in-flight stroke or gesture must not cross into preview: drop it
    // (simplest and predictable — the user is switching modes mid-contact).
    // Clearing touches/pinch keeps pointer state consistent with the onUp
    // guard, which voids any lift that happens while previewing.
    if (this.activePointer !== null) this.cancelStroke();
    if (this.textGesture) { this.textGesture = null; this.clearLive(); }
    this.pendingOutsideTap = null;
    this.touches.clear();
    this.pinchStart = null;
    if (this.selection) await this.commitSelection(); // also clears selGesture
    if (this.imageSel) await this.commitImageSelection(); // also clears imgGesture
    await this.pending; // settle queued commits so the snapshot is complete
    const all = await this.repo.getAllStrokes(this.page.id);
    this.historyStrokes = all;
    const times = new Set<number>();
    for (const s of all) {
      times.add(s.createdAt);
      if (s.deletedAt !== undefined) times.add(s.deletedAt);
    }
    let events = [...times].sort((a, b) => a - b);
    if (events.length > HISTORY_MAX_EVENTS) {
      const sampled = new Set<number>();
      for (let i = 0; i < HISTORY_MAX_EVENTS; i++) {
        sampled.add(events[Math.round((i * (events.length - 1)) / (HISTORY_MAX_EVENTS - 1))]!);
      }
      events = [...sampled]; // insertion order is already ascending
    }
    return { min: events[0] ?? Date.now(), max: Date.now(), events };
  }

  /** Render the page as of time t: createdAt ≤ t and (no tombstone or deletedAt > t). No-op outside preview. */
  previewAsOf(t: number) {
    if (this.historyStrokes === null) return;
    const visible = this.historyStrokes
      .filter((s) => s.createdAt <= t && (s.deletedAt === undefined || s.deletedAt > t))
      // Strip the tombstone from the preview copies: the renderer's draw pass
      // (correctly) skips deletedAt strokes, but a stroke that is tombstoned
      // NOW while visible AT T must still render in the preview.
      .map((s) => { const c = { ...s }; delete c.deletedAt; return c; });
    this.renderer.setStrokes(visible);
    this.redrawStatic();
  }

  /** Leave preview and restore the live stroke set. */
  async exitHistoryPreview(): Promise<void> {
    if (this.historyStrokes === null) return;
    this.historyStrokes = null;
    if (this.destroyed) return;
    this.renderer.setStrokes(await this.repo.getActiveStrokes(this.page.id));
    this.redrawStatic();
  }

  /**
   * Append-only restore (spec §3): strokes visible-at-T but tombstoned-now get
   * their tombstone cleared; strokes live-now but not-visible-at-T get
   * tombstoned. Never rewrites history — one batched `restore` undo op, syncs
   * as ordinary tombstone mutations. Exits preview.
   */
  async restoreToTime(t: number): Promise<void> {
    this.historyStrokes = null; // exit preview; the queued job redraws live state
    this.enqueue(async () => {
      // Compute from FRESH records, not the preview-entry snapshot: a sync
      // pull may have landed rows during the preview (reloadStrokes skips the
      // renderer swap then, but the repo has them) — the reversal must account
      // for those too, e.g. tombstone a remote stroke created after T.
      const all = await this.repo.getAllStrokes(this.page.id);
      const now = Date.now();
      const revivedIds: string[] = [];
      const tombstonedIds: string[] = [];
      for (const s of all) {
        const visibleAtT = s.createdAt <= t && (s.deletedAt === undefined || s.deletedAt > t);
        const liveNow = s.deletedAt === undefined;
        if (visibleAtT && !liveNow) {
          await this.repo.setStrokeDeleted(s.id, null);
          revivedIds.push(s.id);
        } else if (!visibleAtT && liveNow) {
          await this.repo.setStrokeDeleted(s.id, now);
          tombstonedIds.push(s.id);
        }
      }
      if (revivedIds.length || tombstonedIds.length) {
        this.undoStack.push({ type: 'restore', revivedIds, tombstonedIds });
        this.redoStack = [];
        this.emitHistory();
      }
      if (this.destroyed) return;
      this.renderer.setStrokes(await this.repo.getActiveStrokes(this.page.id));
      this.redrawStatic();
    });
    await this.pending;
  }

  // ---------- shape-hold (hold-to-snap) ----------

  /** (Re-)arm the 300ms stillness timer at the given world point. */
  private armHold(x: number, y: number) {
    this.holdArmX = x;
    this.holdArmY = y;
    if (this.holdTimer !== null) clearTimeout(this.holdTimer);
    this.holdTimer = setTimeout(() => {
      this.holdTimer = null;
      this.tryShapeSnap();
    }, HOLD_MS);
  }

  /** Per-move: significant movement (>6 world px from the arm point) reverts any snap and re-arms. */
  private trackHold() {
    if (!this.builder) return;
    const v = this.builder.view();
    if (v.length < 4) return;
    const x = v[v.length - 4]!, y = v[v.length - 3]!;
    if (Math.hypot(x - this.holdArmX, y - this.holdArmY) > HOLD_MOVE_PX) {
      if (this.preSnap) this.revertSnap();
      this.armHold(x, y);
    }
  }

  private tryShapeSnap() {
    // Fire conditions: still down, pen tool, snapping enabled, not already snapped.
    // fitShape itself rejects strokes shorter than 24px.
    if (!this.builder || this.activePointer === null || this.tool !== 'pen' || !this.shapeSnapEnabled || this.preSnap !== null) return;
    const raw = this.builder.toFloat32();
    const fitted = fitShape(raw, { epsilon: SNAP_EPSILON_PX / this.viewport.scale });
    if (!fitted) return;
    this.preSnap = raw;
    this.snapCount = fitted.length / 4;
    this.builder.replaceWith(fitted);
    this.predicted.length = 0;
    this.liveDirty = true;
    this.scheduleLive();
  }

  /** Restore the raw pre-snap points (plus anything drawn since the snap) and let recognition re-arm. */
  private revertSnap() {
    if (!this.builder || !this.preSnap) return;
    const view = this.builder.view();
    const tail = view.subarray(this.snapCount * 4); // points appended after the swap
    const restored = new Float32Array(this.preSnap.length + tail.length);
    restored.set(this.preSnap, 0);
    restored.set(tail, this.preSnap.length);
    this.builder.replaceWith(restored);
    this.preSnap = null;
    this.snapCount = 0;
    this.liveDirty = true;
  }

  private clearHold() {
    if (this.holdTimer !== null) {
      clearTimeout(this.holdTimer);
      this.holdTimer = null;
    }
    this.preSnap = null;
    this.snapCount = 0;
  }

  destroy() {
    if (this.selection) void this.commitSelection(); // persists via repo even after teardown
    if (this.imageSel) void this.commitImageSelection();
    this.destroyed = true;
    this.abort.abort();
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.staticRaf) cancelAnimationFrame(this.staticRaf);
    if (this.retryTimer !== null) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    this.clearHold();
    if (this.unsaved.length) this.flushUnsaved(); // best-effort final save, awaitable via idle()
    this.resizeObs?.disconnect();
    this.renderer.destroy();
    this.staticCanvas.remove();
    this.liveCanvas.remove();
  }
}

function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}
